import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Car, ParkingCircle, Settings, AlertTriangle, CheckCircle2, Plus, Save,
  ChevronDown, RefreshCw, Search, Building2, Layers, CircleDot, Filter,
  FileText, Download, Calendar, User, Hash
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const PARKING_TYPES = ["open", "covered"];
const PARKING_SUBTYPES = ["single", "tandem", "stack"];

const ParkingManagementPage = () => {
  const [tab, setTab] = useState("summary");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Summary data
  const [summary, setSummary] = useState(null);
  
  // Inventory & Pricing
  const [inventory, setInventory] = useState({
    open: { single: 0, tandem: 0, stack: 0 },
    covered: { single: 0, tandem: 0, stack: 0 },
  });
  const [pricing, setPricing] = useState({
    open_single: 0, open_tandem: 0, open_stack: 0,
    covered_single: 0, covered_tandem: 0, covered_stack: 0,
  });
  
  // Reservations & Allotments
  const [reservations, setReservations] = useState([]);
  const [allotments, setAllotments] = useState([]);
  
  // Over-allocation report
  const [overAllocationReport, setOverAllocationReport] = useState(null);
  
  // Allotment dialog
  const [allotmentDialog, setAllotmentDialog] = useState({ open: false, reservation: null });
  const [allotmentItems, setAllotmentItems] = useState([]);
  const [allotmentNotes, setAllotmentNotes] = useState("");
  const [lotteryDate, setLotteryDate] = useState("");
  const [lotteryRef, setLotteryRef] = useState("");

  useEffect(() => {
    fetchProjects();
    fetchOverAllocationReport();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectData();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      toast.error("Failed to fetch projects");
    }
  };

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const [summaryRes, invRes, pricingRes, resRes, allotRes] = await Promise.all([
        axios.get(`${API}/parking/summary/${selectedProject}`),
        axios.get(`${API}/parking/inventory/${selectedProject}`),
        axios.get(`${API}/parking/pricing/${selectedProject}`),
        axios.get(`${API}/parking/reservations?project_id=${selectedProject}`),
        axios.get(`${API}/parking/allotments?project_id=${selectedProject}`),
      ]);
      
      setSummary(summaryRes.data);
      setInventory(invRes.data);
      setPricing(pricingRes.data);
      setReservations(resRes.data);
      setAllotments(allotRes.data);
    } catch (error) {
      console.error("Error fetching parking data:", error);
      toast.error("Failed to fetch parking data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverAllocationReport = async () => {
    try {
      const response = await axios.get(`${API}/parking/over-allocation-report`);
      setOverAllocationReport(response.data);
    } catch (error) {
      console.error("Error fetching over-allocation report:", error);
    }
  };

  const saveInventory = async () => {
    try {
      await axios.put(`${API}/parking/inventory/${selectedProject}`, inventory);
      toast.success("Inventory saved");
      fetchProjectData();
    } catch (error) {
      toast.error("Failed to save inventory");
    }
  };

  const savePricing = async () => {
    try {
      await axios.put(`${API}/parking/pricing/${selectedProject}`, pricing);
      toast.success("Pricing saved");
      fetchProjectData();
    } catch (error) {
      toast.error("Failed to save pricing");
    }
  };

  const openAllotmentDialog = (reservation) => {
    // Pre-fill allotment items from reservation
    const items = reservation.items.map(item => ({
      type: item.type,
      subtype: item.subtype,
      slot_identifier: "",
      quantity: item.quantity,
    }));
    // Expand by quantity
    const expandedItems = [];
    items.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        expandedItems.push({
          type: item.type,
          subtype: item.subtype,
          slot_identifier: "",
        });
      }
    });
    setAllotmentItems(expandedItems);
    setAllotmentDialog({ open: true, reservation });
    setAllotmentNotes("");
    setLotteryDate("");
    setLotteryRef("");
  };

  const saveAllotment = async () => {
    const reservation = allotmentDialog.reservation;
    if (!reservation) return;
    
    try {
      await axios.post(`${API}/parking/allotments`, {
        reservation_id: reservation.reservation_id,
        items: allotmentItems.filter(i => i.slot_identifier),
        lottery_date: lotteryDate || null,
        lottery_reference: lotteryRef,
        notes: allotmentNotes,
      });
      
      toast.success("Allotment recorded");
      setAllotmentDialog({ open: false, reservation: null });
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record allotment");
    }
  };

  const formatINR = (amount) => {
    if (!amount) return "₹0";
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const getProjectName = () => {
    const proj = projects.find(p => p.project_id === selectedProject);
    return proj?.name || "";
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ParkingCircle className="w-7 h-7 text-blue-600" />
              Parking Management
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Manage parking inventory, reservations, and allotments
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-64" data-testid="project-select">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Select Project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchProjectData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Over-allocation Alert */}
        {overAllocationReport && overAllocationReport.projects_with_over_allocation > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">
                  Over-allocation Warning: {overAllocationReport.projects_with_over_allocation} project(s)
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Some projects have more parking reserved than available inventory.
                  Review the reservations before the lottery.
                </p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setTab("reports")}>
                  View Report
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="summary" className="gap-1.5">
              <Layers className="w-4 h-4" /> Summary
            </TabsTrigger>
            <TabsTrigger value="inventory" className="gap-1.5">
              <Settings className="w-4 h-4" /> Setup
            </TabsTrigger>
            <TabsTrigger value="reservations" className="gap-1.5">
              <FileText className="w-4 h-4" /> Reservations
              {reservations.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">{reservations.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="allotments" className="gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Allotments
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Reports
            </TabsTrigger>
          </TabsList>

          {/* ═══ SUMMARY TAB ═══ */}
          <TabsContent value="summary">
            {summary && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Total Inventory</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {Object.values(summary.inventory.open || {}).reduce((a, b) => a + b, 0) +
                         Object.values(summary.inventory.covered || {}).reduce((a, b) => a + b, 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Total Reserved</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {Object.values(summary.reserved.open || {}).reduce((a, b) => a + b, 0) +
                         Object.values(summary.reserved.covered || {}).reduce((a, b) => a + b, 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Available</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {Object.values(summary.available.open || {}).reduce((a, b) => a + b, 0) +
                         Object.values(summary.available.covered || {}).reduce((a, b) => a + b, 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={summary.has_over_allocation ? "border-amber-300 bg-amber-50" : ""}>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Over-allocated</p>
                      <p className={`text-2xl font-bold ${summary.has_over_allocation ? "text-amber-600" : "text-slate-400"}`}>
                        {Object.values(summary.over_allocated.open || {}).reduce((a, b) => a + b, 0) +
                         Object.values(summary.over_allocated.covered || {}).reduce((a, b) => a + b, 0)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Breakdown */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Open Parking */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CircleDot className="w-5 h-5 text-sky-500" /> Open Parking
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Inventory</TableHead>
                            <TableHead className="text-right">Reserved</TableHead>
                            <TableHead className="text-right">Available</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {PARKING_SUBTYPES.map(subtype => {
                            const inv = summary.inventory.open?.[subtype] || 0;
                            const res = summary.reserved.open?.[subtype] || 0;
                            const avail = summary.available.open?.[subtype] || 0;
                            const over = summary.over_allocated.open?.[subtype] || 0;
                            return (
                              <TableRow key={subtype}>
                                <TableCell className="capitalize font-medium">{subtype}</TableCell>
                                <TableCell className="text-right">{inv}</TableCell>
                                <TableCell className="text-right">{res}</TableCell>
                                <TableCell className={`text-right font-medium ${over > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                                  {over > 0 ? `-${over}` : avail}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Covered Parking */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Car className="w-5 h-5 text-indigo-500" /> Covered Parking
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Inventory</TableHead>
                            <TableHead className="text-right">Reserved</TableHead>
                            <TableHead className="text-right">Available</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {PARKING_SUBTYPES.map(subtype => {
                            const inv = summary.inventory.covered?.[subtype] || 0;
                            const res = summary.reserved.covered?.[subtype] || 0;
                            const avail = summary.available.covered?.[subtype] || 0;
                            const over = summary.over_allocated.covered?.[subtype] || 0;
                            return (
                              <TableRow key={subtype}>
                                <TableCell className="capitalize font-medium">{subtype}</TableCell>
                                <TableCell className="text-right">{inv}</TableCell>
                                <TableCell className="text-right">{res}</TableCell>
                                <TableCell className={`text-right font-medium ${over > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                                  {over > 0 ? `-${over}` : avail}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Warnings */}
                {summary.warnings.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" /> Over-allocation Warnings
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {summary.warnings.map((warning, i) => (
                          <li key={i} className="text-sm text-amber-700 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                            {warning.message}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* ═══ INVENTORY SETUP TAB ═══ */}
          <TabsContent value="inventory">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Inventory Setup */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Parking Inventory - {getProjectName()}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Open Parking */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <CircleDot className="w-4 h-4 text-sky-500" /> Open Parking
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {PARKING_SUBTYPES.map(subtype => (
                        <div key={subtype}>
                          <Label className="text-xs capitalize text-slate-500">{subtype}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={inventory.open?.[subtype] || 0}
                            onChange={(e) => setInventory({
                              ...inventory,
                              open: { ...inventory.open, [subtype]: parseInt(e.target.value) || 0 }
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Covered Parking */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Car className="w-4 h-4 text-indigo-500" /> Covered Parking
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {PARKING_SUBTYPES.map(subtype => (
                        <div key={subtype}>
                          <Label className="text-xs capitalize text-slate-500">{subtype}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={inventory.covered?.[subtype] || 0}
                            onChange={(e) => setInventory({
                              ...inventory,
                              covered: { ...inventory.covered, [subtype]: parseInt(e.target.value) || 0 }
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button onClick={saveInventory} className="w-full">
                    <Save className="w-4 h-4 mr-2" /> Save Inventory
                  </Button>
                </CardContent>
              </Card>

              {/* Pricing Setup */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Parking Pricing - {getProjectName()}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Open Parking Prices */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <CircleDot className="w-4 h-4 text-sky-500" /> Open Parking Prices
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {PARKING_SUBTYPES.map(subtype => (
                        <div key={subtype}>
                          <Label className="text-xs capitalize text-slate-500">{subtype}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={pricing[`open_${subtype}`] || 0}
                            onChange={(e) => setPricing({
                              ...pricing,
                              [`open_${subtype}`]: parseFloat(e.target.value) || 0
                            })}
                            placeholder="₹"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Covered Parking Prices */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Car className="w-4 h-4 text-indigo-500" /> Covered Parking Prices
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {PARKING_SUBTYPES.map(subtype => (
                        <div key={subtype}>
                          <Label className="text-xs capitalize text-slate-500">{subtype}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={pricing[`covered_${subtype}`] || 0}
                            onChange={(e) => setPricing({
                              ...pricing,
                              [`covered_${subtype}`]: parseFloat(e.target.value) || 0
                            })}
                            placeholder="₹"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button onClick={savePricing} className="w-full">
                    <Save className="w-4 h-4 mr-2" /> Save Pricing
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ═══ RESERVATIONS TAB ═══ */}
          <TabsContent value="reservations">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Parking Reservations - {getProjectName()}</CardTitle>
              </CardHeader>
              <CardContent>
                {reservations.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No reservations yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Parking Reserved</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reservations.map(res => (
                        <TableRow key={res.reservation_id}>
                          <TableCell className="font-medium">{res.lead_name}</TableCell>
                          <TableCell>{res.unit_number || "-"}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {res.items.map((item, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {item.quantity}× {item.type} {item.subtype}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatINR(res.total_amount)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              res.status === "allotted" ? "default" :
                              res.status === "partially_allotted" ? "secondary" :
                              res.status === "cancelled" ? "destructive" : "outline"
                            }>
                              {res.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {res.status === "reserved" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAllotmentDialog(res)}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Allot
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ ALLOTMENTS TAB ═══ */}
          <TabsContent value="allotments">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Parking Allotments - {getProjectName()}</CardTitle>
              </CardHeader>
              <CardContent>
                {allotments.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No allotments recorded yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Allotted Slots</TableHead>
                        <TableHead>Lottery Date</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allotments.map(allot => (
                        <TableRow key={allot.allotment_id}>
                          <TableCell className="font-medium">{allot.lead_name}</TableCell>
                          <TableCell>{allot.unit_number || "-"}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {allot.items.map((item, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {item.type} {item.subtype}: {item.slot_identifier || "TBD"}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>{allot.lottery_date || "-"}</TableCell>
                          <TableCell>{allot.lottery_reference || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ REPORTS TAB ═══ */}
          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Over-allocation Report (All Projects)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!overAllocationReport || overAllocationReport.projects_with_over_allocation === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">No over-allocations detected</p>
                    <p className="text-sm text-slate-400">All projects have sufficient parking inventory</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {overAllocationReport.report.map(project => (
                      <Card key={project.project_id} className="border-amber-200 bg-amber-50">
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-amber-800 mb-2">{project.project_name}</h3>
                          <ul className="space-y-1">
                            {project.warnings.map((warning, i) => (
                              <li key={i} className="text-sm text-amber-700 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                {warning.message} (Inventory: {warning.inventory}, Reserved: {warning.reserved})
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Allotment Dialog */}
        <Dialog open={allotmentDialog.open} onOpenChange={(open) => !open && setAllotmentDialog({ open: false, reservation: null })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Parking Allotment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {allotmentDialog.reservation && (
                <div className="bg-slate-50 p-3 rounded-lg text-sm">
                  <p><strong>Customer:</strong> {allotmentDialog.reservation.lead_name}</p>
                  <p><strong>Unit:</strong> {allotmentDialog.reservation.unit_number || "N/A"}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Lottery Date</Label>
                  <Input
                    type="date"
                    value={lotteryDate}
                    onChange={(e) => setLotteryDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Lottery Reference</Label>
                  <Input
                    value={lotteryRef}
                    onChange={(e) => setLotteryRef(e.target.value)}
                    placeholder="e.g., LOT-2026-001"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Slot Assignments</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {allotmentItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant="outline" className="min-w-24">
                        {item.type} {item.subtype}
                      </Badge>
                      <Input
                        value={item.slot_identifier}
                        onChange={(e) => {
                          const updated = [...allotmentItems];
                          updated[i].slot_identifier = e.target.value;
                          setAllotmentItems(updated);
                        }}
                        placeholder="Slot ID (e.g., C-15)"
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={allotmentNotes}
                  onChange={(e) => setAllotmentNotes(e.target.value)}
                  placeholder="Any additional notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAllotmentDialog({ open: false, reservation: null })}>
                Cancel
              </Button>
              <Button onClick={saveAllotment}>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Record Allotment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ParkingManagementPage;
